import { authApi } from '../services/authApi';
import { authStorage } from '../services/authStorage';
import { mapStatusToAPI } from './statusUtils';

/**
 * Utility functions for handling optional tests
 */

/**
 * Checks if a test is marked as optional by checking if its title contains '%optional%'
 */
export const isOptionalTest = (testTitle: string): boolean => {
  return testTitle?.toLowerCase().includes('optional') || false;
};

/**
 * Creates a dummy text file with the content "This is an optional test"
 * @returns A File object representing the dummy receipt
 */
export const createDummyReceiptFile = (): File => {
  const content = "This is an optional test";
  const blob = new Blob([content], { type: 'text/plain' });
  return new File([blob], 'optional-test-receipt.txt', { 
    type: 'text/plain',
    lastModified: Date.now()
  });
};

/**
 * Determines the appropriate status for an optional test based on whether it has receipts
 * @param hasReceipt Whether the test already has a receipt uploaded
 * @returns The status to set for the optional test
 */
export const getOptionalTestStatus = (hasReceipt: boolean): 'todo' | 'inProgress' | 'passed' | 'failed' => {
  // If has receipts, mark as Passed
  // If no receipts, mark as Done (which maps to 'passed' status)
  return 'passed';
};

/**
 * Interface for the test ticket used in optional test processing
 */
interface OptionalTestTicket {
  id: number;
  testNumber?: number;
  testSuiteRuleMappingId?: number;
  receipt?: any;
  testName?: string;
}

/**
 * Processes an optional test by uploading dummy receipt and updating status
 * @param ticket The test ticket to mark as optional
 * @param companyId Optional company ID for admin users
 * @returns Promise that resolves when the operation is complete
 */
export const processOptionalTest = async (
  ticket: OptionalTestTicket, 
  companyId?: number
): Promise<void> => {
  const token = authStorage.getAccessToken();
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const dummyFile = createDummyReceiptFile();
  const targetStatus = getOptionalTestStatus(!!ticket.receipt);

  try {
    if (!ticket.receipt) {
      // No receipts uploaded - use uploadReceipt API first, then updateTest to move to passed
      const testRuleInfoId = ticket.testSuiteRuleMappingId || ticket.testNumber || ticket.id;
      
      // Step 1: Upload dummy receipt
      await authApi.uploadReceipt(token, {
        testRuleInfoId: testRuleInfoId,
        receiptFile: dummyFile,
        comment: 'Marked as optional test',
        companyId: companyId,
        isOptional: true,
      });

      // Step 2: Update status to passed
      const mappingId = ticket.testSuiteRuleMappingId || ticket.testNumber || ticket.id;
      await authApi.updateTest(token, {
        testSuiteRuleMappingId: mappingId,
        status: mapStatusToAPI(targetStatus),
        comment: 'Optional test marked as complete',
        companyId: companyId,
        isOptional: true,
      });
    } else {
      // Receipts already uploaded - use updateTest API with dummy file and update status to passed
      const mappingId = ticket.testSuiteRuleMappingId || ticket.testNumber || ticket.id;
      
      await authApi.updateTest(token, {
        testSuiteRuleMappingId: mappingId,
        status: mapStatusToAPI(targetStatus),
        comment: 'Optional test marked as complete',
        receiptFile: dummyFile,
        companyId: companyId,
        isOptional: true,
      });
    }
  } catch (error) {
    console.error('Failed to process optional test:', error);
    throw new Error(`Failed to mark test as optional: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};